import { Center, CenterFactory, CenterProps, Image, Stack } from "@mantine/core";
import { faUser } from "@fortawesome/pro-solid-svg-icons";
import styles from "./navbar.module.scss";
import type { IProps } from "./navbar.types";
import { NavbarItem } from "./components/navbar-item";
import { Icon } from "../icon";
import { polymorphicComponentWrapper } from "../../utilities";
import { NavLink } from "react-router-dom";

/**
 * Navbar component for application navigation.
 * @param {React.ReactNode} props.children - Navigation items and sections to display.
 * @returns {JSX.Element} The Navbar component.
 */
const Navbar = ({ children }: IProps) => {
    return (
        <Stack pb="lg" className={styles["navbar"]} justify="space-between" align="center">
            {children}
        </Stack>
    );
};

/**
 * Main section of the navbar for primary navigation items.
 * @param {React.ReactNode} props.children - Navigation items to display.
 * @returns {JSX.Element} The NavbarMainSection component.
 */
const NavbarMainSection = ({ children }: { children: React.ReactNode }) => {
    return (
        <Stack flex={1} align="center" className={styles["main-section"]}>
            {children}
        </Stack>
    );
};

/**
 * Secondary section of the navbar for secondary navigation items.
 * @param {React.ReactNode} props.children - Navigation items to display.
 * @returns {JSX.Element} The NavbarSecondarySection component.
 */
const NavbarSecondarySection = ({ children }: { children: React.ReactNode }) => {
    return (
        <Stack align="center" className={styles["secondary-section"]}>
            {children}
        </Stack>
    );
};

/**
 * User item component for displaying user information in the navbar.
 * @returns {JSX.Element} The NavbarUserItem component.
 */
const NavbarUserItem = polymorphicComponentWrapper<CenterFactory, CenterProps>(
    "NavbarUserItem",
    ({ className, ...props }, ref) => {
        return (
            <Center ref={ref} className={`${styles["user"]} ${className}`} {...props}>
                <Icon icon={faUser} />
            </Center>
        );
    }
);

/**
 * Logo component for the navbar.
 * @param {string} props.logo - URL or path to the logo image.
 * @param {string} props.to - Route path to navigate to when the logo is clicked.
 * @returns {JSX.Element} The NavbarLogo component.
 */
const NavbarLogo = ({ logo, to }: { logo: string; to: string }) => {
    return (
        <NavLink to={to}>
            <Center classNames={{ root: styles["logo"] }}>
                <Image h={35} fit="contain" src={logo} alt="EdgeTier" />
            </Center>
        </NavLink>
    );
};

Navbar.Item = NavbarItem;
Navbar.Logo = NavbarLogo;
Navbar.MainSection = NavbarMainSection;
Navbar.SecondarySection = NavbarSecondarySection;
Navbar.User = NavbarUserItem;

export { Navbar };
